import React, { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { Switch, Route, Redirect, withRouter } from 'react-router-dom'
import cs from 'classnames'
import loadable from '@loadable/component'

import { withMobile } from 'utils/mobile'

import { getCart, getPrivacityTerms } from 'storage'

import {
    homeErrorSelector,
    homeLoadingSelector,
    homeProductsSelector
} from 'store/selectors/home'
import { bankNameSelector } from 'store/selectors/bank'

import {
    DetailProductTudoPage,
    DetailProductPodPage
} from 'pages/DetailProduct'

import CheckRoutes from './components/ProductRoutes/CheckRoutes'

import StringFormat from 'utils/StringFormat'
import { cartSelector } from 'store/selectors/cart'
import { flipProduct, getProducts } from 'store/actions/home'
import {
    addEditProduct,
    toggleModal,
    openModal,
    clearCart,
    dateAcceptPrivacityTerms,
    deniePrivacityTerms,
    closeModal,
    resetSpokesman
} from 'store/actions/cart'
import { reset } from 'store/actions/lead'

import { TagPushMenu } from 'analytics/header'
import { TagPushCart } from 'analytics/cart'

import Footer from '../../components/Footer'
import Header from '../../components/Header'
import CartBadge from './components/CartBadge'
import CartMenu from './components/CartMenu'
import ProductsBadge from './components/ProductsBadge'
import ProductsMenu from './components/ProductsMenu'
import FooterCart from './components/FooterCart'

import Home from '../Home'

import './HeaderFooterBase.scss'

const Promotion = loadable(() => import('../Promotion'))
const InternalProduct = loadable(() => import('../InternalProduct'))
const NotFound = loadable(() => import('../NotFound'))

const HeaderFooterBase = ({
    cart,
    products,
    bankName,
    history,
    mobile,
    clearCart,
    getProducts,
    getCart,
    acceptPrivacityTerms,
    openCartModal
}) => {
    const [openProducts, setOpenProducts] = useState(false)

    useEffect(() => {
        clearCart()
        getCart(bankName)
        acceptPrivacityTerms()

        getProducts(StringFormat.bankAbbreviation(bankName))
    }, [bankName])

    const validate = () => {
        const cartData = cart.products
        const productsList = products

        const list = cartData.map((p) => {
            const productInfo = productsList.find((listProduct) => {
                return +p.id === +listProduct.code
            })
            return {
                name: productInfo.name.split(' ')[1],
                code: productInfo.code,
                groups: p.groups,
                value: parseFloat(p.value),
                quantity: parseInt(p.qnt)
            }
        })

        closeModal()
        TagPushCart.beginCheckout(list)
        history.push('/proposta')
    }

    const openProductMenu = () => {
        !openProducts &&
            TagPushMenu.interactionMenu(bankName, 'nossos produtos')
        setOpenProducts(!openProducts)
    }

    const openCart = useMemo(() => {
        return cart.open
    }, [cart.open])

    const openCartMenu = () => {
        !openCart && TagPushMenu.interactionMenu(bankName, 'cesta de proposta')
        openCartModal()
    }

    return (
        <div
            className={cs(
                cart.products.length !== 0 && !mobile
                    ? 'header-footer-base margin-bottom'
                    : 'header-footer-base'
            )}
        >
            <Header>
                <ProductsBadge
                    mobile={mobile}
                    onClick={openProductMenu}
                    show={openProducts}
                >
                    {openProducts && (
                        <ProductsMenu
                            bank={bankName}
                            mobile={mobile}
                            products={products}
                            onClick={() => setOpenProducts(false)}
                        />
                    )}
                </ProductsBadge>
                <CartBadge
                    footer={false}
                    badge={cart.products.length}
                    onClick={openCartMenu}
                    show={openCart}
                >
                    {openCart && (
                        <CartMenu
                            cart={cart.products}
                            open={openCartMenu}
                            validate={validate}
                        />
                    )}
                </CartBadge>
            </Header>
            <Switch>
                <Route
                    path="/:bank(bancodobrasil|bradesco)/detalhes/:product(refeicao|alimentacao)"
                    component={InternalProduct}
                />
                <Route
                    path="/:bank(bancodobrasil|bradesco)/detalhes/:product(natal|cultura)"
                    render={() => (
                        <CheckRoutes>
                            <InternalProduct />
                        </CheckRoutes>
                    )}
                />
                <Route
                    path="/:bank(bancodobrasil|bradesco)"
                    exact
                    render={(props) => <Home {...props} />}
                />
                <Route path="/" exact={true}>
                    <Switch>
                        {bankName && <Redirect to={`/${bankName}`} />}
                        <Route render={(props) => <Home {...props} />} />
                    </Switch>
                </Route>
                <Route
                    path="/recuperacao/:cartId/:token"
                    render={(props) => <Home {...props} />}
                />
                <Route path="/destaque/:slug" component={Promotion} />
                <Route
                    path="/detalhes/:product(refeicao|alimentacao|cultura|natal)"
                    render={() => (
                        <CheckRoutes>
                            <InternalProduct />
                        </CheckRoutes>
                    )}
                />
                <Route
                    path="/detalhes/:product(alelo-pod)"
                    render={() => (
                        <CheckRoutes>
                            <DetailProductPodPage />
                        </CheckRoutes>
                    )}
                />
                <Route
                    path="/detalhes/:product(alelo-tudo)"
                    render={() => (
                        <CheckRoutes>
                            <DetailProductTudoPage />
                        </CheckRoutes>
                    )}
                />
                <Route path="/nao-encontrado" component={NotFound} />
                <Redirect to="/" />
            </Switch>
            {cart.products.length !== 0 ? (
                <FooterCart
                    icon="outlined"
                    badge={cart.products.length}
                    products={cart.products}
                    onClick={openCartMenu}
                    validate={validate}
                />
            ) : null}
            <Footer mobile={mobile} />
        </div>
    )
}

const mapStateToProps = (state) => ({
    loading: homeLoadingSelector(state),
    error: homeErrorSelector(state),
    products: homeProductsSelector(state),
    cart: cartSelector(state),
    bankName: bankNameSelector(state)
})

const mapDispatchToProps = (dispatch) => ({
    getProducts: (bank) => dispatch(getProducts(bank)),
    flipProduct: (index) => dispatch(flipProduct(index)),
    addEditProduct: (product) => dispatch(addEditProduct(product)),
    openCartModal: () => {
        dispatch(toggleModal())
    },
    openModal: () => dispatch(openModal()),
    closeModal: () => dispatch(closeModal()),
    clearCart: () => dispatch(clearCart()),
    dateAcceptPrivacityTerms: () => dispatch(dateAcceptPrivacityTerms()),
    deniePrivacityTerms: () => dispatch(deniePrivacityTerms()),
    acceptPrivacityTerms: () => getPrivacityTerms(dispatch),
    getCart: (bank) => getCart(dispatch, bank),
    clearLead: () => {
        dispatch(reset())
        dispatch(resetSpokesman())
    }
})

export default withRouter(
    withMobile(connect(mapStateToProps, mapDispatchToProps)(HeaderFooterBase))
)
